import React, { useState } from "react";
import { AuthModal } from "@/components";
import {
  VerifiedIcon,
  DocumentIcon,
  SelfieIcon,
  PrivacyIcon,
  ModerationIcon,
  HeartSecurityIcon,
} from "@/svgs";
import "./Verification.css";

const VerificationScreen: React.FC = () => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  return (
    <div className="verification-page">
      <section className="verification-hero">
        <div className="v2-container">
          <div className="verification-hero-content">
            <span className="v2-sub-title">VERIFICAÇÃO DE IDENTIDADE</span>

            <h1>
              Perfis reais, <br />
              <span className="v2-gold-text">conexões mais confiáveis</span>
            </h1>

            <p>
              Nosso sistema de verificação foi criado para aumentar a segurança
              da plataforma, reduzir perfis falsos e garantir uma experiência
              mais transparente para todos os usuários.
            </p>

            <div className="verification-badges">
              <div className="verification-badge">
                <VerifiedIcon />
                <span>Perfis reais</span>
              </div>

              <div className="verification-badge">
                <PrivacyIcon />
                <span>Dados protegidos</span>
              </div>

              <div className="verification-badge">
                <ModerationIcon />
                <span>Análise manual</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="verification-process">
        <div className="v2-container">
          <div className="verification-header">
            <span className="v2-sub-title">COMO FUNCIONA</span>

            <h2>
              Processo de <span className="v2-gold-text">verificação</span>
            </h2>

            <p>
              O processo é simples, rápido e feito para garantir autenticidade
              dentro da plataforma.
            </p>
          </div>

          <div className="verification-grid">
            <div className="verification-card">
              <DocumentIcon />

              <h3>1. Envio do documento</h3>

              <p>
                Solicitamos um documento oficial válido com foto para confirmar
                a identidade do usuário.
              </p>
            </div>

            <div className="verification-card">
              <SelfieIcon />

              <h3>2. Selfie de confirmação</h3>

              <p>
                Também pedimos uma selfie em tempo real para validar que a
                pessoa corresponde ao documento enviado.
              </p>
            </div>

            <div className="verification-card">
              <ModerationIcon />

              <h3>3. Revisão manual</h3>

              <p>
                Nossa equipe realiza uma análise manual para garantir maior
                segurança e autenticidade.
              </p>
            </div>

            <div className="verification-card">
              <VerifiedIcon />

              <h3>4. Perfil aprovado</h3>

              <p>
                Após aprovação, o perfil recebe um selo oficial de verificado
                dentro da plataforma.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="verification-info">
        <div className="v2-container">
          <div className="verification-info-wrapper">
            <div className="verification-info-box">
              <h3>Tempo de aprovação</h3>

              <p>
                A maioria das verificações é analisada em poucas horas, podendo
                variar conforme a demanda da plataforma.
              </p>
            </div>

            <div className="verification-info-box">
              <h3>Após a aprovação</h3>

              <p>
                O usuário recebe um selo de perfil verificado, aumentando a
                confiança e credibilidade nas conexões realizadas.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="verification-privacy">
        <div className="v2-container">
          <div className="verification-privacy-content">
            <PrivacyIcon />

            <h2>
              Seus dados <span className="v2-gold-text">estão seguros</span>
            </h2>

            <p>
              As informações enviadas durante a verificação são utilizadas
              exclusivamente para validação de identidade.
            </p>

            <p>
              Seus documentos não ficam públicos e são tratados com segurança,
              confidencialidade e acesso restrito.
            </p>
          </div>
        </div>
      </section>

      <section className="verification-benefits">
        <div className="v2-container">
          <div className="verification-header">
            <span className="v2-sub-title">BENEFÍCIOS</span>

            <h2>
              Vantagens do <span className="v2-gold-text">selo verificado</span>
            </h2>
          </div>

          <div className="verification-benefits-grid">
            <div className="verification-benefit-item">
              <VerifiedIcon />

              <h4>Mais credibilidade</h4>

              <p>
                Perfis verificados transmitem mais confiança para outros
                usuários.
              </p>
            </div>

            <div className="verification-benefit-item">
              <HeartSecurityIcon />

              <h4>Conexões mais seguras</h4>

              <p>
                Reduzimos significativamente a presença de contas falsas e
                golpes.
              </p>
            </div>

            <div className="verification-benefit-item">
              <ModerationIcon />

              <h4>Maior autenticidade</h4>

              <p>Incentivamos uma comunidade formada por pessoas reais.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="verification-badge-preview">
        <div className="v2-container">
          <div className="verification-badge-box">
            <div className="verification-profile-demo">
              <div className="verification-avatar" />

              <div className="verification-profile-info">
                <div className="verification-name">
                  Amanda, 24
                  <span className="verification-check">
                    <span className="pill pill-verified">✔&#xFE0E;</span>
                  </span>
                </div>

                <span>Perfil verificado</span>
              </div>
            </div>

            <div className="verification-badge-text">
              <h2>O selo indica autenticidade</h2>

              <p>
                Usuários verificados recebem um selo especial no perfil,
                demonstrando que passaram pelo processo de validação de
                identidade da plataforma.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="verification-cta">
        <div className="v2-container">
          <div className="verification-cta-content">
            <VerifiedIcon />

            <h2>Mais confiança para todos</h2>

            <p>
              Nosso objetivo é criar uma plataforma mais segura, transparente e
              autêntica para conexões reais.
            </p>

            <button
              className="v2-btn-gold"
              type="button"
              onClick={() => setIsAuthModalOpen(true)}
            >
              Solicitar Acesso Exclusivo
            </button>
          </div>
        </div>
      </section>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />
    </div>
  );
};

export default VerificationScreen;
